import { generateClient, GraphQLResult } from "aws-amplify/api";
import amplifyconfig from "../../../amplifyconfiguration.json";
import { DataProvider, DataProviderArgs } from "../../DataStore/DataProvider";
import { DataSource } from "../../DataStore/LocalDataStore";
import { ChangeSet } from "../../DataStore/ChangeSet";

import * as queries from "../../../graphql/queries";
import * as mutations from "../../../graphql/mutations";

import { getModuleLog, ModuleLog } from "../../Logger";
import { cloneDeep } from "lodash-es";
import fluent, { FluentIterable } from "fluent-iterable";
import { awsToSimpleObject } from "../../../main";

export type GraphQLQueries = {
    get: string;
    list: string;
    create: string;
};

export class GraphQLTable<T extends { id: string | undefined }> extends DataProvider {
    private _all = new Map<string, T>();
    private _allOwnedBy = new Map<string, T[]>();
    protected _log: ModuleLog;

    protected _client = generateClient({
        authMode: "apiKey",
        authToken: amplifyconfig.aws_appsync_apiKey,
    });

    constructor(
        args: DataProviderArgs,
        dependsOn: DataSource[],
        protected _queries: GraphQLQueries,
        protected readonly _toWrapper: (obj: any) => any = (obj: any) => awsToSimpleObject(obj),
        protected readonly _fromWrapper: (obj: any) => any = (obj: any) => cloneDeep(obj)
    ) {
        super(args, dependsOn);
        this._log = getModuleLog("graphql");
    }

    protected async _allAux(filter: any = {}): Promise<FluentIterable<T>> {
        filter._deleted = {
            ne: true,
        };

        let items: T[] = [];
        let nextToken: string | undefined;
        do {
            const result = (await this._client.graphql({
                query: (queries as any)[this._queries.list],
                variables: {
                    filter: filter,
                    limit: 20,
                    nextToken,
                },
            })) as GraphQLResult;

            const r = (result.data as any)[this._queries.list];
            if (r?.items) {
                items = [
                    ...items,
                    ...r.items
                        .filter((x: any) => !x?._deleted)
                        .map((x: any) => this._toWrapper(x) as T),
                ];
            }
            nextToken = r?.nextToken ?? undefined;
        } while (nextToken);

        if (items) {
            return fluent(items);
        }

        return fluent([]);
    }

    public override async all(): Promise<FluentIterable<T>> {
        const result = await this._allAux();
        for (const item of result) {
            this._all.set(item.id as string, item);
        }
        return result;
    }

    public override async read(id: string, ..._args: any[]): Promise<T | undefined> {
        if (this._all.has(id)) {
            return this._all.get(id);
        }

        const query = {
            query: (queries as any)[this._queries.get],
            variables: { id },
        };
        const result = (await this._client.graphql(query)) as GraphQLResult;

        if (result.errors) {
            this._log.error(`Error fetching item with id ${id}: ${result.errors}`);
        }

        const item = this._toWrapper((result.data as any)[this._queries.get]);
        if (item && item.id) {
            this._notifyListeners(new ChangeSet([item], [], []));
            this._all.set(item.id, item);
            return item;
        }
        return undefined;
    }

    public override async allOwnedBy(id: string): Promise<FluentIterable<T>> {
        if (this._allOwnedBy.has(id)) {
            return fluent(this._allOwnedBy.get(id) as T[]);
        }

        const items = await this._allAux({
            owner: {
                contains: id,
            },
            _deleted: {
                ne: true,
            },
        });

        if (items) {
            this._allOwnedBy.set(id, Array.from(items));
            return items;
        }

        return fluent([]);
    }

    public override async create(item: T) {
        const client = generateClient({
            authMode: "userPool",
        });
        const newItem = (await client.graphql({
            query: (mutations as any)[this._queries.create],
            variables: { input: item },
        })) as GraphQLResult;

        return this._toWrapper((newItem.data as any)[this._queries.create]);
    }
    public upsert(_place: T): Promise<T | undefined> {
        throw new Error("Method not implemented.");
    }

    public override async update(_props: any, ..._args: any[]): Promise<any> {
        throw new Error("Method not implemented.");
    }
    public override async delete(_id: string, ..._args: any[]): Promise<void> {
        throw new Error("Method not implemented.");
    }
}
